import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { map, Observable } from 'rxjs';

export interface ContentProFile {
  id: string;
  file_name: string;
  file_ext: string;
  created_by: string;
  created_at: Date;
}

export interface ContentProFileStatus {
  id: string;
  file_name: string;
  created_by: string;
  created_at: Date;
  status: 'pending' | 'complete' | 'quarantined';
  file_ext?: string;
  reason?: string;
}

export type ContentProGetUploadedFile =
  | {
      fileName: string;
      url: string;
      id: string;
    }
  | { fileName: string; error: string };

export interface UploadingFile {
  fileName: string;
  url: string;
  id: string;
  file: File;
  status?: 'pending' | 'complete' | 'quarantined' | 'failed';
  file_ext?: string;
  reason?: string;
  created_by?: string;
  created_at?: Date;
}

export interface ErrorFile {
  fileName: string;
  error: string;
  file: File;
}

export type UploadFileResponse = (UploadingFile | ErrorFile)[];

export type ContentProGetUploadingFilesResponse = {
  data: ContentProGetUploadedFile[];
};

@Injectable({
  providedIn: 'root',
})
export class ContentProLibraryService {
  constructor(private http: HttpClient) {}

  getFiles(clientId: string): Observable<{ data: ContentProFile[] }> {
    const url = `${environment.portalURLBase}/client/${clientId}/content-pro/files`;
    return this.http.get<{ data: ContentProFile[] }>(url, {
      withCredentials: true,
    });
  }

  uploadFile(
    clientId: string,
    files: FileList,
  ): Observable<UploadFileResponse> {
    const filesArray = Array.from(files);

    const contentProUrl = `${environment.portalURLBase}/client/${clientId}/content-pro/files`;
    return this.http
      .post<ContentProGetUploadingFilesResponse>(contentProUrl, {
        files: Object.values(files).map((file) => file.name),
      })
      .pipe(
        map((response) =>
          response.data.map((result, index) => {
            return {
              file: filesArray[index],
              ...result,
            };
          }),
        ),
      );
  }

  checkUploadStatus(
    clientId: string,
    fileIds: string[],
  ): Observable<{ data: ContentProFileStatus[] }> {
    const url = `${environment.portalURLBase}/client/${clientId}/content-pro/uploads/status`;
    return this.http.post<{ data: ContentProFileStatus[] }>(url, {
      ids: fileIds,
    });
  }

  deleteFile(clientId: string, fileId: string): Observable<void> {
    const url = `${environment.portalURLBase}/client/${clientId}/content-pro/files/${fileId}`;
    return this.http.delete<void>(url, {
      withCredentials: true,
    });
  }

  downloadFile(clientId: string, fileId: string): Observable<{ url: string }> {
    const url = `${environment.portalURLBase}/client/${clientId}/content-pro/files/${fileId}/download`;
    return this.http.get<{ url: string }>(url, {
      withCredentials: true,
    });
  }
}
